import { z } from 'zod'
import { hexColorSchema } from '../validators/hex-color.validator.js'

/**
 * A static background zone (grass, sand, water, structures) on a floor plan.
 * Discriminated on `kind` so geometry fields are required for the active shape
 * (polygon → `points`, path → `d`, rect → corner + size, circle → center +
 * radius, ellipse → center + two radii). Shared source of truth for the web
 * editor and persisted JSON.
 *
 * `kind` is declared only inside the union below. There used to be a
 * `ZONE_KINDS` list and a `zoneKindSchema` beside it, which nothing read and
 * which sat one kind behind the union for as long as `ellipse` existed — a
 * second list of the same fact, with nothing to keep it honest. The union is
 * the list.
 */

/** Semantic category — drives the default fill in the builder. */
export const ZONE_TYPES = [
  'grass',
  'sand',
  'water',
  'concrete',
  'brown',
  'floor',
  'structure',
  'tree',
  'other',
] as const

export const zoneTypeSchema = z.enum(ZONE_TYPES)

export type ZoneType = z.infer<typeof zoneTypeSchema>

/**
 * What a zone is painted with: a color, or `none` for a shape that is only an
 * outline — which is what a drawn line is, and what three quarters of the zones
 * in production are.
 *
 * `none` is why a zone's paint cannot simply be a color. TypeScript still sees
 * `string` here (a literal union with `string` collapses to `string`), so this
 * buys a runtime guarantee and a name for the rule, not a compile-time one.
 */
const zonePaintSchema = z.union([z.literal('none'), hexColorSchema])

/** Unreadable paint becomes no paint, rather than costing the plan its zone. */
export const zoneFillSchema = zonePaintSchema.catch('none')
export const zoneStrokeSchema = zonePaintSchema.optional().catch(undefined)

/** Shared paint/metadata on every zone kind. */
const zoneStyle = {
  id: z.string(),
  zType: zoneTypeSchema.optional(),
  label: z.string().optional(),
  fill: zoneFillSchema,
  stroke: zoneStrokeSchema,
  strokeWidth: z.number().optional(),
  dash: z.array(z.number()).optional(),
}

export const zoneSchema = z.discriminatedUnion('kind', [
  z.object({
    ...zoneStyle,
    kind: z.literal('polygon'),
    points: z.array(z.number()),
    tension: z.number().optional(),
    /** When false, render as an open polyline (a drawn line). Defaults to true. */
    closed: z.boolean().optional(),
  }),
  z.object({
    ...zoneStyle,
    kind: z.literal('path'),
    d: z.string(),
  }),
  z.object({
    ...zoneStyle,
    kind: z.literal('rect'),
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }),
  z.object({
    ...zoneStyle,
    kind: z.literal('circle'),
    x: z.number(),
    y: z.number(),
    radius: z.number(),
  }),
  z.object({
    ...zoneStyle,
    /**
     * An oval, and a circle drawn by hand.
     *
     * Separate from `circle` rather than `circle` gaining an optional second
     * radius: a circle has one radius and that is the whole of it, and the
     * imported plans full of `circle` zones should not have to be read as
     * "an ellipse whose radii happen to match". The name and the fields follow
     * `primitive.dto`, which already had this shape for furniture.
     */
    kind: z.literal('ellipse'),
    x: z.number(),
    y: z.number(),
    radiusX: z.number(),
    radiusY: z.number(),
  }),
])

export type Zone = z.infer<typeof zoneSchema>
